/**
 * WordPress dependencies
 */
import { PanelBody } from '@safe-wordpress/components';
import { createInterpolateElement } from '@safe-wordpress/element';
import { _x, sprintf } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { listify } from '@nab/utils';
import type {
	QueryArgSetting,
	TestedUrlWithQueryArgsScopeRule,
} from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';
import { getScopeExplanation } from '../explanation';
import { useExperimentAttribute } from '../../../hooks';

export const TestedUrlWithQueryArgsScope = (): JSX.Element | null => {
	// INFO. We’re currently setting only one rule of this type, so we “find” instead of “filter.”
	const rule = ( useExperimentAttribute( 'scope' ) || [] ).find(
		( r ): r is TestedUrlWithQueryArgsScopeRule =>
			r.attributes.type === 'tested-url-with-query-args'
	);
	const status = useExperimentAttribute( 'status' ) ?? 'running';

	if ( ! rule || ! rule.attributes.value.args.length ) {
		return null;
	}

	return (
		<PanelBody
			className="nab-experiment-scope"
			title={ _x( 'Test Scope', 'text', 'nelio-ab-testing' ) }
		>
			<p>
				{ getScopeExplanation(
					'tested-url-with-query-args',
					[ rule ],
					status
				) }
			</p>
			<ul className="nab-experiment-tested-url-with-query-args-scope__list">
				{ rule.attributes.value.args.map( ( arg ) => (
					<li key={ arg.name }>
						{ createInterpolateElement(
							// eslint-disable-next-line @wordpress/valid-sprintf
							sprintf(
								CONDITION_LABELS[ arg.condition ],
								`<code>${ arg.name }</code>`,
								listify(
									'or',
									arg.value
										.split( '\n' )
										.map( ( v ) => `<code>${ v }</code>` )
								)
							),
							{ code: <code /> }
						) }
					</li>
				) ) }
			</ul>
		</PanelBody>
	);
};

// =======
// HELPERS
// =======

type Condition = QueryArgSetting[ 'condition' ];
const CONDITION_LABELS: Record< Condition, string > = {
	/* translators: %s: Parameter name. */
	exists: _x( '%s exists', 'text', 'nelio-ab-testing' ),
	/* translators: %s: Parameter name. */
	'does-not-exist': _x( '%s does not exist', 'text', 'nelio-ab-testing' ),
	/* translators: %1$s: Parameter name. %2$s: Parameter value. */
	'is-equal-to': _x( '%1$s is equal to %2$s', 'text', 'nelio-ab-testing' ),
	/* translators: %1$s: Parameter name. %2$s: Parameter value. */
	'is-not-equal-to': _x(
		'%1$s is not equal to %2$s',
		'text',
		'nelio-ab-testing'
	),
	/* translators: %1$s: Parameter name. %2$s: Parameter value. */
	contains: _x( '%1$s contains %2$s', 'text', 'nelio-ab-testing' ),
	/* translators: %1$s: Parameter name. %2$s: Parameter value. */
	'does-not-contain': _x(
		'%1$s does not contain %2$s',
		'text',
		'nelio-ab-testing'
	),
	/* translators: %1$s: Parameter name. %2$s: List of parameter values. */
	'is-any-of': _x( '%1$s is any of %2$s', 'text', 'nelio-ab-testing' ),
	/* translators: %1$s: Parameter name. %2$s: List of parameter values. */
	'is-none-of': _x( '%1$s is none of %2$s', 'text', 'nelio-ab-testing' ),
};
